import React, { useCallback, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useHistory, useRouteMatch } from 'react-router';
import { generatePath } from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';

import lineEndpoint from 'app/api/services/lineEndpoint';

import { URLS } from 'app/config/urls';
import { clearFares, clearSelectedLine, fetchLine } from 'app/store/slices/line';
import { errorHandler } from 'app/utils/errorHandler';

import Loading from 'app/layouts/Loading';

import { makeStyles } from '@material-ui/core';
import { KeyboardDatePicker } from '@material-ui/pickers';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Grid from '@material-ui/core/Grid';
import InputAdornment from '@material-ui/core/InputAdornment';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';

import DefaultTable from '../components/DefaultTable';
import PageWrapper from '../components/PageWrapper';
import SaveBox from '../components/SaveBox';

import { newFareInitialValues } from '../form-configs/fare-initial-values';
import { fareListColumns } from '../table-configs/columns-schemas';

import notification from 'app/config/notification';
import { newFareValidationSchema } from '../form-configs/validation-schemas';
import { validateFormikForm } from '../../../utils/forms/functions';
import styles from './styles';
import moment from 'moment';

const FareList = () => {
    const match = useRouteMatch();
    const classes = useStyles();
    const history = useHistory();
    const dispatch = useDispatch();

    const cdLinha = React.useRef(match.params.cdLinha).current;

    const [loading, setLoading] = useState(false);
    const [addMode, setAddMode] = useState(false);
    const [dataToTable, setDataToTable] = useState([]);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [fares, setFares] = useState({});

    const selectedLine = useSelector((state) => state.line.selected);

    const updateFares = useCallback(async () => {
        const params = { page: page, size: pageSize, request: { cdLinha } };
        const response = await lineEndpoint.getFares(params);
        if (response && response.status === 200 && response.data) {
            setFares(response.data);
        }
    }, [cdLinha, page, pageSize]);

    useEffect(() => {
        setLoading(true);
        (async () => {
            const promisses = [];
            if (!selectedLine) promisses.push(dispatch(fetchLine(cdLinha)));
            await updateFares();
            if (promisses.length > 0) await Promise.all(promisses);
            setLoading(false);
        })();
        return () => {
            dispatch(clearSelectedLine());
            dispatch(clearFares());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, page, pageSize]);

    const prepareData = (values) => {
        if (!values) return [];
        return values.map((entry) => {
            const temp = { ...entry };
            if (temp.dtEmissao) temp.dtEmissao = moment(temp.dtEmissao).format('DD/MM/YYYY');
            if (temp.dtValidade) temp.dtValidade = moment(temp.dtValidade).format('DD/MM/YYYY');

            return temp;
        });
    };

    useEffect(() => {
        setDataToTable(prepareData(fares.content));
    }, [fares, addMode]);

    const validateForm = (functionValidation) => {
        functionValidation.then((res) => {
            if (Object.keys(res).length > 0) {
                notification('O(s) campo(s) sinalizado(s) precisam de revisão.', {
                    toastId: 'VALIDATION',
                    type: 'error',
                });
            }
        });
    };

    const handleSubmitNewFare = async (values) => {
        const { cdLinha } = match.params;
        const preparedData = {
            ...values,
            cdLinha,
        };

        try {
            setLoading(true);
            const response = await lineEndpoint.saveFare(preparedData);
            await updateFares();
            if (response.status === 200)
                notification('Tarifa cadastrada com sucesso', {
                    toastId: 'SUCCESSFUL_SAVE_FARE',
                    type: 'success',
                });
        } catch (error) {
            errorHandler(error.response?.status, error.response?.data?.message);
        } finally {
            formik.handleReset();
            setAddMode(false);
            setLoading(false);
        }
    };

    const handleChangePage = (e, page) => {
        setPage(page);
    };

    const handleChangePageSize = (e) => {
        setPageSize(parseInt(e.target.value));
    };

    const handleGoBack = () => {
        history.push(generatePath(URLS.LINES_MANAGE, { isStrip: null }));
    };

    const formik = useFormik({
        initialValues: newFareInitialValues,
        validationSchema: newFareValidationSchema,
        validateOnBlur: false,
        validateOnChange: false,
        onSubmit: (values, formProps) => {
            validateFormikForm(formProps, newFareValidationSchema).then((res) => {
                if (Object.keys(res).length > 0) {
                    notification('O(s) campo(s) sinalizado(s) precisam de revisão.', {
                        toastId: 'VALIDATION',
                        type: 'error',
                    });
                } else {
                    handleSubmitNewFare(values, formProps);
                }
            });
        },
    });

    return (
        <>
            <PageWrapper
                title="Cadastro / Edição de tarifas"
                actionButton={
                    <Button
                        variant="contained"
                        color="primary"
                        disableElevation
                        className={classes.addButton}
                        onClick={() => setAddMode((prevState) => !prevState)}>
                        {addMode ? 'Cancelar' : 'Adicionar'}
                    </Button>
                }>
                {loading && <Loading />}
                <Grid container spacing={4}>
                    <Grid item xs={12} container>
                        <Grid item xs={12} sm={7}>
                            <TextField
                                label="Linha"
                                name="deNome"
                                value={selectedLine?.deDescricao}
                                disabled
                                fullWidth
                                variant="filled"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <DefaultTable
                            columns={fareListColumns}
                            rows={dataToTable}
                            paginationProps={{
                                rowsPerPageOptions: [5, 10, 25],
                                count: fares?.totalElements || 0,
                                rowsPerPage: pageSize,
                                page: page,
                                onPageChange: handleChangePage,
                                onRowsPerPageChange: handleChangePageSize,
                            }}>
                            {addMode && <NewFareTableRow formikInstance={formik} />}
                        </DefaultTable>
                    </Grid>
                    <Grid item xs={12}>
                        <SaveBox
                            removeSpacing
                            firstButtonLabel="Voltar"
                            onSave={() => {
                                validateForm(formik.validateForm());
                                formik.handleSubmit();
                            }}
                            onCancel={handleGoBack}
                        />
                    </Grid>
                </Grid>
            </PageWrapper>
        </>
    );
};

const useStyles = makeStyles(styles);

export default FareList;

const NewFareTableRow = ({ formikInstance: formik }) => {
    const configField = {
        variant: 'outlined',
        inputVariant: 'outlined',
        margin: 'dense',
        size: 'small',
    };

    return (
        <TableRow>
            <TableCell size="small">
                <TextField
                    name="nuValor"
                    type="number"
                    value={formik.values.nuValor}
                    onChange={(e) => {
                        let value = Number(e.target.value);
                        if (value > 999.99) {
                            value = 999.99;
                        }
                        formik.setFieldValue('nuValor', value);
                    }}
                    onBlur={(e) => {
                        let value = Number(e.target.value);
                        formik.setFieldValue('nuValor', value.toFixed(2));
                        formik.handleBlur(e);
                    }}
                    error={formik.touched.nuValor && Boolean(formik.errors.nuValor)}
                    helperText={formik.touched.nuValor && formik.errors.nuValor}
                    {...configField}
                    InputProps={{
                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                    }}
                    style={{
                        maxWidth: 120,
                    }}
                    inputProps={{
                        form: {
                            autoComplete: 'off',
                        },
                    }}
                />
            </TableCell>
            <TableCell size="small">
                <KeyboardDatePicker
                    as={TextField}
                    format="DD/MM/YYYY"
                    name="dtEmissao"
                    invalidDateMessage="Favor informar uma data válida"
                    value={formik.values.dtEmissao}
                    onChange={(e) => e && formik.setFieldValue('dtEmissao', e.format())}
                    error={!!formik.errors.dtEmissao}
                    helperText={formik.errors.dtEmissao ? formik.errors.dtEmissao : ''}
                    {...configField}
                />
            </TableCell>
            <TableCell size="small">
                <KeyboardDatePicker
                    as={TextField}
                    format="DD/MM/YYYY"
                    name="dtValidade"
                    invalidDateMessage="Favor informar uma data válida"
                    value={formik.values.dtValidade}
                    onChange={(e) => e && formik.setFieldValue('dtValidade', e.format())}
                    error={!!formik.errors.dtValidade}
                    helperText={formik.errors.dtValidade ? formik.errors.dtValidade : ''}
                    {...configField}
                />
            </TableCell>
            <TableCell size="small">
                <TextField
                    name="nuProcesso"
                    value={formik.values.nuProcesso}
                    onChange={(e) => formik.setFieldValue('nuProcesso', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                        maxLength: 30,
                    }}
                    error={!!formik.errors.nuProcesso}
                    helperText={formik.errors.nuProcesso ? formik.errors.nuProcesso : ''}
                    {...configField}
                />
            </TableCell>
            <TableCell size="small">
                <Checkbox checked name="boVigente" {...configField} />
            </TableCell>
        </TableRow>
    );
};
